import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  EyeIcon,
  PencilIcon,
  UserIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { UserGroupIcon as UserGroupIconSolid } from '@heroicons/react/24/solid';

const TeamCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('team'); // team, department, all
  const [events, setEvents] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    // Simulate fetching team calendar data
    const fetchData = () => {
      const mockTeamMembers = [
        {
          id: 'user001',
          name: 'Dr. Sarah Johnson',
          title: 'Senior Physician',
          department: 'Cardiology',
          team: 'Cardiac Surgery Team',
          email: 'sarah.johnson@apollo.com',
          avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100',
          status: 'available',
          workingHours: { start: '08:00', end: '17:00' },
          timezone: 'EST'
        },
        {
          id: 'user002',
          name: 'Dr. Michael Chen',
          title: 'Neurologist',
          department: 'Neurology',
          team: 'Neuro Care Team',
          email: 'michael.chen@apollo.com',
          avatar: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100',
          status: 'busy',
          workingHours: { start: '09:00', end: '18:00' },
          timezone: 'EST'
        },
        {
          id: 'user003',
          name: 'Nurse Lisa Wong',
          title: 'Head Nurse',
          department: 'Emergency',
          team: 'Emergency Response Team',
          email: 'lisa.wong@apollo.com',
          avatar: 'https://images.unsplash.com/photo-1594824475198-1394a5d1e4e3?w=100',
          status: 'available',
          workingHours: { start: '07:00', end: '19:00' },
          timezone: 'EST'
        },
        {
          id: 'user004',
          name: 'Dr. James Wilson',
          title: 'Resident Doctor',
          department: 'Internal Medicine',
          team: 'General Medicine Team',
          email: 'james.wilson@apollo.com',
          avatar: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=100',
          status: 'on-leave',
          workingHours: { start: '08:00', end: '16:00' },
          timezone: 'EST'
        },
        {
          id: 'user005',
          name: 'Dr. Emily Davis',
          title: 'Pediatrician',
          department: 'Pediatrics',
          team: 'Child Care Team',
          email: 'emily.davis@apollo.com',
          avatar: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=100',
          status: 'available',
          workingHours: { start: '08:30', end: '17:30' },
          timezone: 'EST'
        },
        {
          id: 'user006',
          name: 'Dr. Robert Martinez',
          title: 'Surgeon',
          department: 'Surgery',
          team: 'Surgical Team Alpha',
          email: 'robert.martinez@apollo.com',
          avatar: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=100',
          status: 'in-surgery',
          workingHours: { start: '06:00', end: '15:00' },
          timezone: 'EST'
        }
      ];

      const mockDepartments = [
        { id: 'cardiology', name: 'Cardiology', memberCount: 15, color: 'blue' },
        { id: 'neurology', name: 'Neurology', memberCount: 12, color: 'purple' },
        { id: 'emergency', name: 'Emergency', memberCount: 25, color: 'red' },
        { id: 'internal-medicine', name: 'Internal Medicine', memberCount: 20, color: 'green' },
        { id: 'pediatrics', name: 'Pediatrics', memberCount: 18, color: 'orange' },
        { id: 'surgery', name: 'Surgery', memberCount: 22, color: 'indigo' }
      ];

      const mockEvents = [
        {
          id: 'team001',
          title: 'Morning Rounds',
          description: 'Daily patient rounds with medical team',
          type: 'rounds',
          department: 'Cardiology',
          team: 'Cardiac Surgery Team',
          organizer: 'Dr. Sarah Johnson',
          attendees: ['Dr. Sarah Johnson', 'Dr. Michael Chen', 'Nurse Lisa Wong'],
          start: new Date(2024, 11, 15, 8, 0),
          end: new Date(2024, 11, 15, 9, 30),
          location: 'Ward 3A',
          status: 'confirmed',
          priority: 'high',
          color: 'blue'
        },
        {
          id: 'team002',
          title: 'Department Meeting',
          description: 'Weekly department coordination meeting',
          type: 'meeting',
          department: 'Neurology',
          team: 'Neuro Care Team',
          organizer: 'Dr. Michael Chen',
          attendees: ['Dr. Michael Chen', 'Dr. James Wilson', 'Dr. Emily Davis'],
          start: new Date(2024, 11, 15, 14, 0),
          end: new Date(2024, 11, 15, 15, 0),
          location: 'Conference Room B',
          status: 'confirmed',
          priority: 'medium',
          color: 'purple'
        },
        {
          id: 'team003',
          title: 'Emergency Drill',
          description: 'Hospital-wide emergency response training',
          type: 'training',
          department: 'Emergency',
          team: 'Emergency Response Team',
          organizer: 'Nurse Lisa Wong',
          attendees: ['All Staff'],
          start: new Date(2024, 11, 16, 10, 0),
          end: new Date(2024, 11, 16, 11, 30),
          location: 'Hospital Wide',
          status: 'confirmed',
          priority: 'high',
          color: 'red'
        },
        {
          id: 'team004',
          title: 'Surgery Schedule Review',
          description: 'Review and coordinate upcoming surgeries',
          type: 'planning',
          department: 'Surgery',
          team: 'Surgical Team Alpha',
          organizer: 'Dr. Robert Martinez',
          attendees: ['Dr. Robert Martinez', 'Dr. Sarah Johnson', 'Surgical Nurses'],
          start: new Date(2024, 11, 17, 13, 0),
          end: new Date(2024, 11, 17, 14, 30),
          location: 'OR Planning Room',
          status: 'tentative',
          priority: 'high',
          color: 'indigo'
        },
        {
          id: 'team005',
          title: 'Pediatric Case Review',
          description: 'Review complex pediatric cases',
          type: 'review',
          department: 'Pediatrics',
          team: 'Child Care Team',
          organizer: 'Dr. Emily Davis',
          attendees: ['Dr. Emily Davis', 'Dr. James Wilson', 'Pediatric Nurses'],
          start: new Date(2024, 11, 18, 11, 0),
          end: new Date(2024, 11, 18, 12, 0),
          location: 'Pediatric Conference Room',
          status: 'confirmed',
          priority: 'medium',
          color: 'orange'
        },
        {
          id: 'team006',
          title: 'Interdisciplinary Conference',
          description: 'Cross-department patient care coordination',
          type: 'conference',
          department: 'Internal Medicine',
          team: 'General Medicine Team',
          organizer: 'Dr. James Wilson',
          attendees: ['Multi-department staff'],
          start: new Date(2024, 11, 19, 15, 0),
          end: new Date(2024, 11, 19, 16, 30),
          location: 'Main Auditorium',
          status: 'confirmed',
          priority: 'medium',
          color: 'green'
        }
      ];

      setTeamMembers(mockTeamMembers);
      setDepartments(mockDepartments);
      setEvents(mockEvents);
      setLoading(false);
    };

    setTimeout(fetchData, 500);
  }, []);

  const eventTypes = [
    { id: 'all', name: 'All Events' },
    { id: 'rounds', name: 'Rounds' },
    { id: 'meeting', name: 'Meetings' },
    { id: 'training', name: 'Training' },
    { id: 'planning', name: 'Planning' },
    { id: 'review', name: 'Reviews' },
    { id: 'conference', name: 'Conferences' }
  ];

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.organizer.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTeam = selectedTeam === 'all' || event.team === selectedTeam;
    const matchesDepartment = selectedDepartment === 'all' || event.department === selectedDepartment;
    const matchesType = selectedEventType === 'all' || event.type === selectedEventType;

    return matchesSearch && matchesTeam && matchesDepartment && matchesType;
  });

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.department.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment = selectedDepartment === 'all' || member.department === selectedDepartment;

    return matchesSearch && matchesDepartment;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'busy':
        return 'bg-red-100 text-red-800';
      case 'on-leave':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-surgery':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'busy':
        return <XCircleIcon className="h-4 w-4" />;
      case 'on-leave':
        return <InformationCircleIcon className="h-4 w-4" />;
      case 'in-surgery':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const getEventColor = (event) => {
    const colors = {
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      red: 'bg-red-500',
      indigo: 'bg-indigo-500',
      orange: 'bg-orange-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500'
    };
    return colors[event.color] || 'bg-blue-500';
  };

  const getEventBgColor = (event) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      purple: 'bg-purple-50 border-purple-200',
      red: 'bg-red-50 border-red-200',
      indigo: 'bg-indigo-50 border-indigo-200',
      orange: 'bg-orange-50 border-orange-200',
      green: 'bg-green-50 border-green-200',
      yellow: 'bg-yellow-50 border-yellow-200'
    };
    return colors[event.color] || 'bg-blue-50 border-blue-200';
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getDaysInWeek = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const openMemberModal = (member) => {
    setSelectedMember(member);
    setShowMemberModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <UserGroupIconSolid className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Team Calendar</h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Coordinate schedules and manage team events
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <Link
                  to="/calendar/schedule"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Schedule Event
                </Link>
                <Link
                  to="/calendar"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  All Calendars
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Week Navigation */}
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateWeek(-1)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                >
                  <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <div className="text-base sm:text-lg font-semibold text-gray-900 min-w-[180px] sm:min-w-[200px] text-center">
                  Week of {currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <button
                  onClick={() => navigateWeek(1)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                >
                  <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>

              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
              >
                This Week
              </button>
            </div>

            {/* View Toggle */}
            <div className="flex items-center justify-center sm:justify-start">
              <div className="inline-flex rounded-md shadow-sm">
                {[
                  { id: 'team', name: 'Team View', short: 'Team' },
                  { id: 'department', name: 'Department', short: 'Dept' },
                  { id: 'all', name: 'All Staff', short: 'All' }
                ].map((viewType) => (
                  <button
                    key={viewType.id}
                    onClick={() => setView(viewType.id)}
                    className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium ${
                      view === viewType.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } ${
                      viewType.id === 'team' ? 'rounded-l-md' :
                      viewType.id === 'all' ? 'rounded-r-md -ml-px' :
                      '-ml-px'
                    } border border-gray-300`}
                  >
                    <span className="hidden sm:inline">{viewType.name}</span>
                    <span className="sm:hidden">{viewType.short}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search events or people..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team
                  </label>
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Teams</option>
                    {Array.from(new Set(teamMembers.map(m => m.team))).map(team => (
                      <option key={team} value={team}>{team}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Type
                  </label>
                  <select
                    value={selectedEventType}
                    onChange={(e) => setSelectedEventType(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {eventTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedDepartment('all');
                    setSelectedTeam('all');
                    setSelectedEventType('all');
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Team Members Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Team Members</h3>
                <div className="space-y-3">
                  {filteredMembers.slice(0, 8).map(member => (
                    <div
                      key={member.id}
                      onClick={() => openMemberModal(member)}
                      className="flex items-center p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <img
                        className="h-10 w-10 rounded-full"
                        src={member.avatar}
                        alt={member.name}
                      />
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {member.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {member.title}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(member.status)}`}>
                        {getStatusIcon(member.status)}
                        <span className="ml-1 capitalize">{member.status.replace('-', ' ')}</span>
                      </span>
                    </div>
                  ))}

                  {filteredMembers.length > 8 && (
                    <div className="text-center pt-2">
                      <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                        View All ({filteredMembers.length - 8} more)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Department Summary */}
            <div className="mt-6 bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Departments</h3>
                <div className="space-y-3">
                  {departments.map(dept => (
                    <div key={dept.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full bg-${dept.color}-500 mr-3`}></div>
                        <span className="text-sm font-medium text-gray-900">{dept.name}</span>
                      </div>
                      <span className="text-sm text-gray-500">{dept.memberCount}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="lg:col-span-3">
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {/* Week Header */}
              <div className="grid grid-cols-8 gap-px bg-gray-200">
                <div className="bg-gray-50 py-3 px-2">
                  <div className="text-xs font-medium text-gray-500 text-center">Time</div>
                </div>
                {getDaysInWeek(currentDate).map((day, index) => (
                  <div key={index} className="bg-gray-50 py-3 px-2">
                    <div className="text-center">
                      <div className="text-xs font-medium text-gray-500">
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className={`text-sm font-semibold mt-1 ${
                        isToday(day) ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {day.getDate()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Week Body */}
              <div className="bg-white">
                {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(hour => (
                  <div key={hour} className="grid grid-cols-8 gap-px border-b border-gray-100">
                    <div className="bg-gray-50 py-2 px-2 text-right">
                      <div className="text-xs text-gray-500">
                        {hour === 12 ? '12:00 PM' : hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`}
                      </div>
                    </div>
                    {getDaysInWeek(currentDate).map((day, dayIndex) => {
                      const dayEvents = getEventsForDate(day).filter(event => {
                        const eventHour = new Date(event.start).getHours();
                        return eventHour === hour;
                      });

                      return (
                        <div key={dayIndex} className="bg-white p-1 min-h-[60px] relative">
                          {dayEvents.map(event => (
                            <div
                              key={event.id}
                              className={`text-xs p-2 rounded mb-1 cursor-pointer hover:opacity-80 ${getEventBgColor(event)}`}
                              title={`${event.title} - ${event.organizer}`}
                            >
                              <div className="flex items-center">
                                <div className={`w-2 h-2 rounded-full mr-1 ${getEventColor(event)}`}></div>
                                <span className="truncate font-medium">{event.title}</span>
                              </div>
                              <div className="text-gray-600 truncate">
                                {formatTime(event.start)} - {formatTime(event.end)}
                              </div>
                              <div className="text-gray-500 truncate">
                                {event.organizer}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Events List */}
            <div className="mt-6 bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Upcoming Events</h3>
                  <Link
                    to="/calendar/schedule"
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Schedule New
                  </Link>
                </div>

                <div className="space-y-4">
                  {filteredEvents
                    .filter(event => new Date(event.start) > new Date())
                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                    .slice(0, 5)
                    .map(event => (
                      <div key={event.id} className={`p-4 rounded-lg border ${getEventBgColor(event)}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <div className={`w-3 h-3 rounded-full mr-2 ${getEventColor(event)}`}></div>
                              <h4 className="text-sm font-semibold text-gray-900">{event.title}</h4>
                              <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                event.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                event.status === 'tentative' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {event.status}
                              </span>
                            </div>

                            <p className="text-sm text-gray-600 mb-2">{event.description}</p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-500">
                              <div className="flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {formatTime(event.start)} - {formatTime(event.end)}
                              </div>
                              <div className="flex items-center">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                {event.start.toLocaleDateString()}
                              </div>
                              <div className="flex items-center">
                                <MapPinIcon className="h-3 w-3 mr-1" />
                                {event.location}
                              </div>
                            </div>

                            <div className="mt-2 flex items-center text-xs text-gray-500">
                              <UserIcon className="h-3 w-3 mr-1" />
                              Organized by {event.organizer}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                              title="View details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                              title="Edit event"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {filteredEvents.filter(event => new Date(event.start) > new Date()).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No upcoming events
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Member Details Modal */}
      {showMemberModal && selectedMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Team Member Details
              </h3>
              <button
                onClick={() => setShowMemberModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <img
                  className="h-16 w-16 rounded-full"
                  src={selectedMember.avatar}
                  alt={selectedMember.name}
                />
                <div className="ml-4">
                  <h4 className="text-xl font-bold text-gray-900">
                    {selectedMember.name}
                  </h4>
                  <p className="text-gray-600">{selectedMember.title}</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedMember.status)}`}>
                    {getStatusIcon(selectedMember.status)}
                    <span className="ml-1 capitalize">{selectedMember.status.replace('-', ' ')}</span>
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Department</div>
                  <div className="flex items-center">
                    <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{selectedMember.department}</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Team</div>
                  <div className="flex items-center">
                    <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{selectedMember.team}</span>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Working Hours</div>
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {selectedMember.workingHours.start} - {selectedMember.workingHours.end} ({selectedMember.timezone})
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Contact</div>
                  <div className="text-sm text-gray-900">{selectedMember.email}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowMemberModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <Link
                to={`/calendar/schedule?attendee=${selectedMember.id}`}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Schedule Meeting
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCalendar;
